import { useEffect, useState } from 'react'
import { toLang } from '../../config/locales'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react'
import { useUiStore } from '../../store/uiStore'
import { useCartStore } from '../../store/cartStore'
import Button from '../ui/Button'
import { formatKr } from '../../lib/shop/format'
import {
  shopifyCartUrl,
  shopifyStoreDomain,
  shopifyCartCreate,
  shopifyCartGetCheckoutUrl,
  shopifyCartLinesUpdate,
  shopifyCartLinesRemove,
} from '../../lib/shop/checkout'
import { track } from '../../lib/analytics/track'

const FREE_SHIPPING_THRESHOLD = 499

export default function CartDrawer() {
  const { lang = 'en' } = useParams<{ lang: string }>()
  const locale = toLang(lang)
  const { t } = useTranslation()
  const reduce = useReducedMotion()

  const isOpen = useUiStore((s) => s.isCartDrawerOpen)
  const setOpen = useUiStore((s) => s.setCartDrawer)
  const items = useCartStore((s) => s.items)
  const shopifyCartId = useCartStore((s) => s.shopifyCartId)
  const lineIdMap = useCartStore((s) => s.lineIdMap)
  const setShopifyCartId = useCartStore((s) => s.setShopifyCartId)
  const setLineIds = useCartStore((s) => s.setLineIds)
  const clearCart = useCartStore((s) => s.clearCart)
  const updateQuantity = useCartStore((s) => s.updateQuantity)
  const removeItem = useCartStore((s) => s.removeItem)
  const total = useCartStore((s) => s.cartTotal())
  const count = useCartStore((s) => s.cartCount())

  const close = () => setOpen(false)

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const [checkingOut, setCheckingOut] = useState(false)

  /**
   * Syncs a quantity change to Shopify. The local state is updated optimistically
   * before this is called; if the Shopify cart has expired we fall through
   * gracefully - the next checkout attempt will create a fresh cart.
   */
  const syncQuantity = (variantId: string, quantity: number) => {
    const cartId = shopifyCartId
    const lineId = lineIdMap[variantId]
    if (!cartId || !lineId) return

    if (quantity <= 0) {
      shopifyCartLinesRemove(cartId, [lineId])
        .then((r) => {
          if (r) setLineIds(r.lines)
          // If null: cart expired - cleared automatically when next checkout runs.
        })
        .catch(() => {})
    } else {
      shopifyCartLinesUpdate(cartId, [{ lineId, quantity }])
        .then((r) => {
          if (r) setLineIds(r.lines)
        })
        .catch(() => {})
    }
  }

  const handleUpdateQuantity = (variantId: string, quantity: number) => {
    updateQuantity(variantId, quantity) // optimistic
    syncQuantity(variantId, quantity)
  }

  const handleRemoveItem = (variantId: string) => {
    track('cart_remove_item', { variant_id: variantId })
    removeItem(variantId) // optimistic
    syncQuantity(variantId, 0)
  }

  const handleCheckout = async () => {
    setCheckingOut(true)
    track('checkout_handoff', { item_count: count, value: total })
    try {
      // 1. Reuse the persisted Shopify cart for a fresh checkout URL.
      if (shopifyCartId) {
        const url = await shopifyCartGetCheckoutUrl(shopifyCartId)
        if (url) {
          clearCart()
          window.location.href = url
          return
        }
        setShopifyCartId(null) // expired - lineIdMap also cleared by store
      }

      // 2. Build a fresh Shopify cart from the current local items.
      const result = await shopifyCartCreate(items)
      if (result) {
        clearCart()
        window.location.href = result.checkoutUrl
        return
      }

      // 3. Last resort: /cart permalink (no Cart API dependency).
      const fallback = shopifyCartUrl(items, shopifyStoreDomain())
      if (fallback) {
        clearCart()
        window.location.href = fallback
      }
    } finally {
      setCheckingOut(false)
    }
  }

  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - total)
  const freeShippingPct = Math.min(100, (total / FREE_SHIPPING_THRESHOLD) * 100)

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          aria-modal="true"
          role="dialog"
          aria-label={t('cart.title')}
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label={t('cart.close')}
            onClick={close}
            className="absolute inset-0 bg-mocha/80 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: reduce ? 0 : '100%' }}
            animate={{ x: 0 }}
            exit={{ x: reduce ? 0 : '100%' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex h-full w-full max-w-md flex-col bg-ivory shadow-[0_20px_60px_rgba(45,28,18,0.35)]"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-mocha/10 px-6 py-5">
              <h2 className="font-display text-2xl text-mocha">
                {t('cart.title')}
                {count > 0 && <span className="ml-2 font-body text-sm text-mocha/40">({count})</span>}
              </h2>
              <button
                type="button"
                onClick={close}
                aria-label={t('cart.close')}
                className="text-mocha/50 transition-colors hover:text-gold"
              >
                <X size={22} />
              </button>
            </div>

            {items.length === 0 ? (
              /* Empty state */
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 text-center">
                <ShoppingBag size={40} className="text-mocha/20" />
                <p className="font-body text-sm text-mocha/55">{t('cart.empty')}</p>
                <Button as={Link} to={`/${lang}/shop`} variant="secondary" size="sm" onClick={close}>
                  {t('cart.emptyCta')}
                </Button>
              </div>
            ) : (
              <>
                {/* Items */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  <ul className="space-y-5">
                    {items.map((item) => (
                      <li key={item.variantId} className="flex gap-4">
                        <Link
                          to={`/${lang}/shop/${item.id}`}
                          onClick={close}
                          className="h-20 w-20 flex-shrink-0 overflow-hidden border border-mocha/10 bg-ivory-dark/30"
                        >
                          {item.image ? (
                            <img src={item.image} alt={item.title} className="h-full w-full object-contain p-1.5" />
                          ) : (
                            <span className="flex h-full items-center justify-center text-mocha/20">
                              <ShoppingBag size={20} />
                            </span>
                          )}
                        </Link>

                        <div className="flex min-w-0 flex-1 flex-col">
                          <Link
                            to={`/${lang}/shop/${item.id}`}
                            onClick={close}
                            className="font-display text-sm text-mocha transition-colors hover:text-gold line-clamp-2"
                          >
                            {item.title}
                          </Link>
                          <span className="mt-0.5 font-body text-xs text-gold">
                            {formatKr(String(item.price), locale)}
                          </span>

                          <div className="mt-auto flex items-center justify-between pt-2">
                            {/* Quantity stepper */}
                            <div className="flex items-center border border-mocha/15">
                              <button
                                type="button"
                                aria-label={t('cart.decrease')}
                                onClick={() => handleUpdateQuantity(item.variantId, item.quantity - 1)}
                                className="flex h-7 w-7 items-center justify-center text-mocha/60 transition-colors hover:text-gold"
                              >
                                <Minus size={13} />
                              </button>
                              <span className="min-w-[1.75rem] text-center font-body text-sm text-mocha">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                aria-label={t('cart.increase')}
                                onClick={() => handleUpdateQuantity(item.variantId, item.quantity + 1)}
                                className="flex h-7 w-7 items-center justify-center text-mocha/60 transition-colors hover:text-gold"
                              >
                                <Plus size={13} />
                              </button>
                            </div>

                            <button
                              type="button"
                              aria-label={t('cart.remove')}
                              onClick={() => handleRemoveItem(item.variantId)}
                              className="text-mocha/35 transition-colors hover:text-gold"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer */}
                <div className="border-t border-mocha/10 px-6 py-5">
                  {/* Free shipping progress */}
                  <div className="mb-4">
                    <p className="mb-2 font-body text-xs text-mocha/55">
                      {remaining > 0
                        ? t('cart.freeShippingRemaining', { amount: formatKr(String(remaining), locale) })
                        : t('cart.freeShippingUnlocked')}
                    </p>
                    <div className="h-1 w-full overflow-hidden rounded-full bg-mocha/10">
                      <motion.div
                        className="h-full rounded-full bg-gold"
                        initial={false}
                        animate={{ width: `${freeShippingPct}%` }}
                        transition={{ duration: reduce ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                  </div>

                  <div className="mb-1 flex items-baseline justify-between">
                    <span className="font-body text-sm text-mocha/60">{t('cart.subtotal')}</span>
                    <span className="font-display text-xl text-mocha">{formatKr(String(total), locale)}</span>
                  </div>
                  <p className="mb-4 text-right font-body text-[11px] text-mocha/40">{t('cart.vatNote')}</p>

                  <Button
                    as="button"
                    type="button"
                    disabled={checkingOut}
                    className="w-full justify-center"
                    onClick={handleCheckout}
                  >
                    {checkingOut ? '...' : t('cart.checkout')}
                  </Button>
                  <button
                    type="button"
                    onClick={close}
                    className="mt-3 w-full font-body text-xs text-mocha/50 underline-offset-4 transition-colors hover:text-gold hover:underline"
                  >
                    {t('cart.continue')}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
